import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001',
  headers: { 'Content-Type': 'application/json' },
});

// Interceptor de requisição (adiciona token)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Interceptor de resposta (trata 401 e SESSION_INVALIDATED de forma NUCLEAR)
api.interceptors.response.use(
  res => res,
  err => {
    const status = err.response?.status;
    const errorCode = err.response?.data?.code;
    const requestUrl = err.config?.url || '';

    // ⭐ CORRIGIDO: nunca aplicar a limpeza "nuclear" numa tentativa de LOGIN que falhou
    // (senha errada, ex.) — isso é erro normal, o próprio Login.js já mostra a mensagem.
    // Também não faz sentido redirecionar se a pessoa já está na tela de login.
    const isLoginRequest = requestUrl.includes('/api/auth/login');
    const alreadyOnLoginPage = window.location.pathname === '/login';

    if ((status === 401 || errorCode === 'SESSION_INVALIDATED') && !isLoginRequest && !alreadyOnLoginPage) {
      // 🛑 LIMPEZA NUCLEAR: Remove TUDO do armazenamento do navegador
      localStorage.clear();
      sessionStorage.clear();
      window.location.replace('/login');
    }
    return Promise.reject(err);
  }
);

// ============================================
// CONVERSAS
// ============================================
export const getConversations = (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  return api.get(`/api/conversations${params ? '?' + params : ''}`);
};
export const getConversation = (id) => api.get(`/api/conversations/${id}`);

// ⭐ NOVO: respostas rápidas (mensagens prontas pro atendente reaproveitar)
export const getQuickReplies = () => api.get('/api/quick-replies');
export const createQuickReply = (data) => api.post('/api/quick-replies', data);
export const updateQuickReply = (id, data) => api.put(`/api/quick-replies/${id}`, data);
export const deleteQuickReply = (id) => api.delete(`/api/quick-replies/${id}`);
export const assignConversation = (id, data) => api.post(`/api/conversations/${id}/assign`, data);
export const updateConversationStatus = (id, status) => api.post(`/api/conversations/${id}/status`, { status });
export const toggleAiMode = (id, ai_mode) => api.post(`/api/conversations/${id}/ai-mode`, { ai_mode });
export const getAppointmentsByConversation = (conversationId) => api.get(`/api/appointments/by-conversation/${conversationId}`);
export const markConversationAsRead = (id) => api.put(`/api/conversations/${id}/mark-read`);
// ============================================
// MENSAGENS
// ============================================
export const getMessages = (conversationId) => api.get(`/api/messages/conversation/${conversationId}`);
export const sendMessage = (conversationId, to, text) =>
  api.post('/api/messages/send', { conversation_id: conversationId, to, text });
export const sendMediaMessage = (conversationId, to, file, caption = '') => {
  const formData = new FormData();
  formData.append('conversation_id', conversationId);
  formData.append('to', to);
  formData.append('caption', caption);
  formData.append('file', file);
  return api.post('/api/messages/send-media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// ============================================
// USUÁRIOS
// ============================================
export const getUsers = () => api.get('/api/users');
export const getUser = (id) => api.get(`/api/users/${id}`);
export const createUser = (data) => api.post('/api/users', data);
export const updateUser = (id, data) => api.put(`/api/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/api/users/${id}`);
export const getMe = () => api.get('/api/users/me');

// ============================================
// SETORES
// ============================================
export const getSectors = () => api.get('/api/sectors');
export const createSector = (data) => api.post('/api/sectors', data);
export const updateSector = (id, data) => api.put(`/api/sectors/${id}`, data);
export const deleteSector = (id) => api.delete(`/api/sectors/${id}`);

// ============================================
// TAGS
// ============================================
export const getTags = () => api.get('/api/tags');
export const createTag = (data) => api.post('/api/tags', data);
export const deleteTag = (id) => api.delete(`/api/tags/${id}`);
export const assignTagsToContact = (contactId, tagIds) => 
  api.post(`/api/tags/contact/${contactId}`, { tag_ids: tagIds });
export const getContactTags = (contactId) => api.get(`/api/tags/contact/${contactId}`);

// ============================================
// FUNIL DE VENDAS
// ============================================
export const getFunnelStages = () => api.get('/api/funnel/stages');
export const updateFunnelStage = (conversationId, stageId) => 
  api.post(`/api/funnel/conversation/${conversationId}/stage`, { stage_id: stageId });
export const getFunnelHistory = (conversationId) => 
  api.get(`/api/funnel/conversation/${conversationId}/history`);

// ============================================
// NOTAS INTERNAS
// ============================================
export const getInternalNotes = (conversationId) => 
  api.get(`/api/internal-notes/conversation/${conversationId}`);
export const createInternalNote = (data) => api.post('/api/internal-notes', data);
export const updateInternalNote = (id, data) => api.put(`/api/internal-notes/${id}`, data);
export const deleteInternalNote = (id) => api.delete(`/api/internal-notes/${id}`);

// ============================================
// CAMPANHAS
// ============================================
export const getCampaigns = () => api.get('/api/campaigns');
export const getCampaign = (id) => api.get(`/api/campaigns/${id}`);
export const createCampaign = (data) => api.post('/api/campaigns', data);
export const launchCampaign = (id) => api.post(`/api/campaigns/${id}/launch`);
export const deleteCampaign = (id) => api.delete(`/api/campaigns/${id}`);
export const getCampaignMetrics = (id) => api.get(`/api/campaigns/${id}/metrics`);
export const getCampaignRecipients = (id, status) => 
  api.get(`/api/campaigns/${id}/recipients${status ? '?status=' + status : ''}`);
export const resendCampaign = (id, data) => api.post(`/api/campaigns/${id}/resend`, data);
export const getCampaignEstimatedCost = (id) => api.get(`/api/campaigns/${id}/estimated-cost`);

// ============================================
// LEADS (CRM)
// ============================================
export const getLeads = (filters) => {
  const params = new URLSearchParams(filters).toString();
  return api.get(`/api/leads${params ? '?' + params : ''}`);
};
export const getLead = (id) => api.get(`/api/leads/${id}`);
export const createLead = (data) => api.post('/api/leads', data);
export const updateLead = (id, data) => api.put(`/api/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/api/leads/${id}`);
export const getLeadStats = () => api.get('/api/leads/stats');

// ============================================
// TAREFAS
// ============================================
export const getTasks = (filters) => {
  const params = new URLSearchParams(filters).toString();
  return api.get(`/api/tasks${params ? '?' + params : ''}`);
};
export const createTask = (data) => api.post('/api/tasks', data);
export const updateTask = (id, data) => api.put(`/api/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/api/tasks/${id}`);
export const getOverdueTasks = () => api.get('/api/tasks/overdue');

// ============================================
// AUTOMAÇÕES
// ============================================
export const getAutomations = () => api.get('/api/automations');
export const createAutomation = (data) => api.post('/api/automations', data);
export const updateAutomation = (id, data) => api.put(`/api/automations/${id}`, data);
export const deleteAutomation = (id) => api.delete(`/api/automations/${id}`);

// ============================================
// RELATÓRIOS
// ============================================
export const getDashboardMetrics = () => api.get('/api/reports/dashboard');
export const getResponseTimeMetrics = (filters) => {
  const params = new URLSearchParams(filters).toString();
  return api.get(`/api/reports/response-time${params ? '?' + params : ''}`);
};
export const getFunnelConversion = () => api.get('/api/reports/funnel-conversion');
export const getVolumeBySector = () => api.get('/api/reports/volume-by-sector');
export const getDailyVolume = (days = 7) => api.get(`/api/reports/daily-volume?days=${days}`);
export const getWeekdayPerformance = () => api.get('/api/reports/weekday-performance');

// ============================================
// WHATSAPP NUMBERS (Multi-números)
// ============================================
export const getWhatsAppNumbers = (filters) => {
  const params = new URLSearchParams(filters).toString();
  return api.get(`/api/whatsapp-numbers${params ? '?' + params : ''}`);
};
export const getWhatsAppNumber = (id) => api.get(`/api/whatsapp-numbers/${id}`);
export const createWhatsAppNumber = (data) => api.post('/api/whatsapp-numbers', data);
export const updateWhatsAppNumber = (id, data) => api.put(`/api/whatsapp-numbers/${id}`, data);
export const deleteWhatsAppNumber = (id) => api.delete(`/api/whatsapp-numbers/${id}`);

// ============================================
// PHONE NUMBERS (Números por setor)
// ============================================
export const getPhoneNumbers = () => api.get('/api/phone-numbers');
export const createPhoneNumber = (data) => api.post('/api/phone-numbers', data);
export const updatePhoneNumber = (id, data) => api.put(`/api/phone-numbers/${id}`, data);
export const deletePhoneNumber = (id) => api.delete(`/api/phone-numbers/${id}`);

// ============================================
// AI AGENTS
// ============================================
export const getAIAgents = () => api.get('/api/ai-agents');
export const createAIAgent = (data) => api.post('/api/ai-agents', data);
export const updateAIAgent = (id, data) => api.put(`/api/ai-agents/${id}`, data);
export const deleteAIAgent = (id) => api.delete(`/api/ai-agents/${id}`);
export const testAIAgent = (id) => api.post(`/api/ai-agents/${id}/test`);

// ============================================
// AUTOMATION RULES (Regras estilo Wati)
// ============================================
export const getAutomationRules = () => api.get('/api/automation-rules');
export const getAutomationRule = (id) => api.get(`/api/automation-rules/${id}`);
export const createAutomationRule = (data) => api.post('/api/automation-rules', data);
export const updateAutomationRule = (id, data) => api.put(`/api/automation-rules/${id}`, data);
export const deleteAutomationRule = (id) => api.delete(`/api/automation-rules/${id}`);

// ============================================
// SEGMENTS (Segmentos de contatos)
// ============================================
export const getSegments = () => api.get('/api/segments');
export const createSegment = (data) => api.post('/api/segments', data);
export const updateSegment = (id, data) => api.put(`/api/segments/${id}`, data);
export const deleteSegment = (id) => api.delete(`/api/segments/${id}`);
export const getSegmentContacts = (id) => api.get(`/api/segments/${id}/contacts`);
export const addContactsToSegment = (id, contact_wa_ids) => 
  api.post(`/api/segments/${id}/contacts`, { contact_wa_ids });
export const removeContactFromSegment = (segmentId, wa_id) => 
  api.delete(`/api/segments/${segmentId}/contacts/${wa_id}`);

// ============================================
// AD ACCOUNTS (Contas de Anúncios)
// ============================================
export const getAdAccounts = () => api.get('/api/ad-accounts');
export const createAdAccount = (data) => api.post('/api/ad-accounts', data);
export const updateAdAccount = (id, data) => api.put(`/api/ad-accounts/${id}`, data);
export const deleteAdAccount = (id) => api.delete(`/api/ad-accounts/${id}`);

// ============================================
// VARIÁVEIS DE TEMPLATE
// ============================================
export const getTemplateVariables = () => api.get('/api/template-variables');
export const createTemplateVariable = (data) => api.post('/api/template-variables', data);
export const updateTemplateVariable = (id, data) => api.put(`/api/template-variables/${id}`, data);
export const deleteTemplateVariable = (id) => api.delete(`/api/template-variables/${id}`);
export const substituteVariables = (text, contactData) => 
  api.post('/api/template-variables/substitute', { text, contact_data: contactData });
export const deleteTemplate = (id) => api.delete(`/api/templates/${id}`);
export const archiveTemplate = (id) => api.put(`/api/templates/${id}/archive`);
export const unarchiveTemplate = (id) => api.put(`/api/templates/${id}/unarchive`);
export const syncTemplatesFromMeta = () => api.post('/api/templates/sync-from-meta');
export const getApprovedTemplates = (phoneNumberId) =>
  api.get(`/api/templates/approved${phoneNumberId ? '?phone_number_id=' + phoneNumberId : ''}`);
export const sendTemplateMessage = (conversationId, to, templateId) =>
  api.post('/api/messages/send-template', { conversation_id: conversationId, to, template_id: templateId });

// ============================================
// ⭐ TRANSFERÊNCIA DE CONVERSAS
// ============================================
export const transferConversation = (conversationId, data) => 
  api.post(`/api/conversations/${conversationId}/transfer`, data);
export const getAvailableSectors = (conversationId) => 
  api.get(`/api/conversations/${conversationId}/available-sectors`);
export const getAvailableOperators = (sectorId) => 
  api.get(`/api/conversations/sector/${sectorId}/available-operators`);
export const getTransferHistory = (conversationId) => 
  api.get(`/api/conversations/${conversationId}/transfer-history`);

// ============================================
// PERFIL DO USUÁRIO
// ============================================
export const getMyProfile = () => api.get('/api/profile/me');
export const updateMyProfile = (data) => api.put('/api/profile/me', data);
export const getMyConversations = (filters) => {
  const params = new URLSearchParams(filters).toString();
  return api.get(`/api/profile/my-conversations${params ? '?' + params : ''}`);
};
export const getOnlineOperators = (sectorId) => 
  api.get(`/api/profile/online-operators${sectorId ? '?sector_id=' + sectorId : ''}`);


export const getFunnelBoardData = () => api.get('/api/funnel-board/data');

// ============================================
// CONTATOS
// ============================================
export const createContact = (data) => api.post('/api/contacts', data);
export const updateContact = (id, data) => api.put(`/api/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/api/contacts/${id}`);
export const importContacts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/contacts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// ============================================
// LOGOUT
// ============================================
export const logout = () => api.post('/api/auth/logout');


// ============================================
// AGENDAMENTOS
// ============================================
export const getSpecialties = () => api.get('/api/professionals/specialties');
export const getAvailableDates = (specialty) => api.get(`/api/professionals/available-dates?specialty=${encodeURIComponent(specialty)}`);
export const getProfessionalsByDate = (specialty, date) => api.get(`/api/professionals/by-date?specialty=${encodeURIComponent(specialty)}&date=${date}`);
export const getProfessionalSlots = (professionalId, date) => api.get(`/api/professionals/${professionalId}/slots?date=${date}`);
export const bookAppointmentFromChat = (data) => api.post('/api/professionals/book', data);

export const getProfessionalAvailability = (professionalId) => api.get(`/api/professionals/${professionalId}/availability`);
export const createProfessionalAvailability = (professionalId, data) => api.post(`/api/professionals/${professionalId}/availability`, data);
export const deleteProfessionalAvailability = (id) => api.delete(`/api/professionals/availability/${id}`);

export default api;
